import {
  Injectable,
  OnDestroy,
  Injector,
  effect,
  EffectRef,
  runInInjectionContext,
  inject,
} from '@angular/core';
import { StateService } from './state.service';
import { HttpClient } from '@angular/common/http';
import { NotificationStateService } from './notification.state';
import { tap, catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { ApiService } from '../services/api.service';
import { ErrorHandlerService } from '../services/error-handler.service';

export interface ServerModel {
  product_code: string;
  variant_code: string;
  product_description: string;
  variant_description: string;
  spec1: string;
  spec2: string;
  spec3: string;
  spec4: string;
  price: number;
  free_days: number;
}

export interface ServerLocation {
  city: string;
  country: string;
  models: { [key: string]: ServerModel };
}

export interface ServerProvider {
  name: string;
  locations: { [key: string]: ServerLocation };
}

export interface ServerProviders {
  [key: string]: ServerProvider;
}

export interface Server {
  id: string;
  name: string;
  name_dashboard?: string;
  name_url?: string;
  city?: string;
  country?: string;
  created_on?: string;
  end?: string;
  auto_renew?: boolean;
}

export interface ServerResponse {
  id: string;
  provider: string;
  location: string;
  models: {
    [key: string]: ServerModel;
  };
}

export interface ServerState {
  servers: Server[];
  hasServers: boolean;
  selectedServer: Server | null;
  providers: { [key: string]: ServerResponse[] };
  selectedLocation: string | null;
  selectedProvider: string | null;
  serverProducts: ServerProviders;
  isLoaded: boolean;
  isLoadingProducts: boolean;
  error: string | null;
}

const initialServerState: ServerState = {
  servers: [],
  hasServers: false,
  selectedServer: null,
  providers: {},
  selectedLocation: null,
  selectedProvider: 'CON',
  serverProducts: {},
  isLoaded: false,
  isLoadingProducts: false,
  error: null,
};

@Injectable({
  providedIn: 'root',
})
export class ServerStateService extends StateService<ServerState> implements OnDestroy {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationStateService);
  private injector = inject(Injector);
  private apiService = inject(ApiService);
  private errorHandler = inject(ErrorHandlerService);

  // Computed signals for derived state
  readonly servers = this.selectSlice(state => state.servers);
  readonly hasServers = this.selectSlice(state => state.isLoaded && state.servers.length > 0);
  readonly selectedServer = this.selectSlice(state => state.selectedServer);
  readonly providers = this.selectSlice(state => state.providers);
  readonly selectedLocation = this.selectSlice(state => state.selectedLocation);
  readonly selectedProvider = this.selectSlice(state => state.selectedProvider);
  readonly serverProducts = this.selectSlice(state => state.serverProducts);
  readonly isLoaded = this.selectSlice(state => state.isLoaded);
  readonly isLoadingProducts = this.selectSlice(state => state.isLoadingProducts);
  readonly error = this.selectSlice(state => state.error);

  // Store cleanup effects
  private cleanup: EffectRef[] = [];

  // Computed signal for products as array for backward compatibility.
  // Uses JSON equality to prevent re-triggering effects when
  // unrelated state changes cause a new array reference.
  readonly productsArray = this.selectSlice(
    state => {
      const products = state.serverProducts;
      if (!products) return [];

      const result: any[] = [];
      Object.entries(products).forEach(([providerId, provider]) => {
        Object.entries(provider.locations).forEach(([locationId, location]) => {
          Object.entries(location.models).forEach(([modelId, model]) => {
            result.push({
              ...model,
              provider: providerId,
              location: locationId,
              size: modelId,
              city: location.city,
              country: location.country,
            });
          });
        });
      });
      return result;
    },
    { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
  );

  // Computed signal for available models based on selected location
  readonly availableModels = this.selectSlice(
    state => {
      if (!state.selectedProvider || !state.selectedLocation) {
        return [];
      }

      const provider = state.serverProducts[state.selectedProvider];
      if (!provider || !provider.locations[state.selectedLocation]) {
        return [];
      }

      const location = provider.locations[state.selectedLocation];
      const modelEntries = Object.entries(location.models);
      const models = modelEntries.map(([key, model]) => {
        return {
          ...model,
          size: key,
        };
      });
      return models;
    },
    { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
  );

  constructor() {
    super(initialServerState);
  }

  // Make this public so it can be called when needed
  loadServerProducts(): void {
    // Check if products are already loaded or being loaded
    if (Object.keys(this.serverProducts()).length > 0 || this.isLoadingProducts()) {
      return;
    }

    this.setState({ isLoadingProducts: true, error: null });
    this.http
      .get<ServerProviders>(this.apiService.getUrl('products/servers'))
      .pipe(
        tap(products => {
          this.setState({
            serverProducts: products,
            isLoadingProducts: false,
          });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoadingProducts: false, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  selectProvider(providerId: string): void {
    this.setState({ selectedProvider: providerId, selectedLocation: null });
  }

  selectLocation(locationId: string): void {
    this.setState({ selectedLocation: locationId });
  }

  loadServers(): void {
    if (this.isLoaded()) {
      return;
    }
    this.http
      .get<Server[]>(this.apiService.getUrl('servers'))
      .pipe(
        tap(servers => {
          this.setState({ servers, isLoaded: true });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoaded: true, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  loadServer(uuid: string): void {
    this.http
      .get<Server>(`${this.apiService.getUrl(`servers/${uuid}`)}`)
      .pipe(
        tap(server => {
          this.selectServer(server);
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  saveOrder(orderrow_uuid: string | undefined, data: any): void {
    // Set isLoaded to true to indicate loading state has started
    this.setState({ error: null, isLoaded: true });

    let line_item: Record<string, any> = {};
    if (orderrow_uuid !== undefined) {
      line_item['id'] = orderrow_uuid;
    }
    line_item['variant_code'] = data.server.variant_code;
    line_item['location'] = data.server.location;
    line_item['name'] = data.server.name;
    line_item['volume'] = data.server.volume;
    line_item['backup'] = data.server.backup;
    line_item['software'] = data.software;

    let line_items = [line_item];
    let order = {
      line_items: line_items,
      auto_renew: data.financial.auto_renew,
    };
    let body = { order: order };

    this.http
      .post(this.apiService.getUrl('orders'), body)
      .pipe(
        tap(() => {
          // Set isLoaded to false to indicate loading state has completed
          this.setState({ error: null, isLoaded: false });
          this.notificationService.success('Server successfully saved');
          // Reload servers to get the updated list
          this.loadServers();
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);

          // Make sure we set the error state before showing the notification
          // to ensure the component can react to the error state change
          this.setState({ error: msg, isLoaded: false });

          // Show the error notification after the state has been updated
          setTimeout(() => {
            this.notificationService.error(msg);
          }, 0);

          return EMPTY;
        })
      )
      .subscribe();
  }

  selectServer(server: Server | null): void {
    this.setState({ selectedServer: server });
  }

  clearSelectedServer(): void {
    this.setState({ selectedServer: null });
  }

  setupServerAvailabilityCheck(): void {
    // Create an effect to watch servers and check server availability if needed
    runInInjectionContext(this.injector, () => {
      const effectRef = effect(() => {
        if (!this.isLoaded()) {
          this.checkServerAvailability();
        }
      });
      this.cleanup.push(effectRef);
    });
  }

  checkServerAvailability(): void {
    // Load just the servers list to check if user has any
    this.http
      .get<any>(this.apiService.getUrl('servers'))
      .pipe(
        tap(response => {
          // Check if we have any servers in the response array
          this.setState({
            servers: response || [],
            isLoaded: true,
          });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({
            isLoaded: true,
            error: msg,
          });
          return EMPTY;
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.cleanup.forEach(effectRef => effectRef.destroy());
  }
}
